/**
 * The signed-in user's scheduling delegations (migration 1780). Only the three field roles can hold one, so the query
 * is skipped for everyone else. `isDelegate` drives the nav, the route gate and the rep picker; the database enforces
 * the same rules on every read and write.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../utils/roleUtils";
import { getMySchedulingDelegations } from "../services/schedulingDelegates.service";

const DELEGATE_ROLES = new Set(["msr", "territorymanager", "regionalmanager"]);

export function canHoldSchedulingDelegation(
  role: string | null | undefined,
): boolean {
  return DELEGATE_ROLES.has(normalizeRole(role));
}

export function useSchedulingDelegations() {
  const { user } = useAuth();
  const eligible = !!user?.id && canHoldSchedulingDelegation(user?.role);
  const q = useQuery({
    queryKey: ["my-scheduling-delegations", user?.id],
    queryFn: getMySchedulingDelegations,
    enabled: eligible,
    staleTime: 60_000,
  });
  const delegations = useMemo(
    () => (eligible ? (q.data ?? []) : []),
    [eligible, q.data],
  );
  const teamPositionIds = useMemo(
    () => new Set(delegations.flatMap((d) => d.team_position_ids)),
    [delegations],
  );
  return {
    delegations,
    isDelegate: delegations.length > 0,
    teamPositionIds,
    isLoading: eligible && q.isLoading,
  };
}
